import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ShoppingCart, Menu, X, User, LogOut, ChevronDown, Leaf } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const { cart } = useCart();
  const location = useLocation();
  const navigate = useNavigate();

  const navLinks = [
    { href: '/shop', label: 'Shop Boxes' },
    { href: '/build-a-box', label: 'Build Your Box' },
    { href: '/farms', label: 'Our Farms' },
    { href: '/delivery', label: 'Delivery' },
    { href: '/about', label: 'About' },
    { href: '/faq', label: 'FAQ' },
  ];

  const dashHref = user?.role === 'admin' ? '/admin' : user?.role === 'farm_partner' ? '/farm/dashboard' : '/dashboard';
  const dashLabel = user?.role === 'admin' ? 'Admin Portal' : user?.role === 'farm_partner' ? 'Farm Dashboard' : 'Dashboard';

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <header data-testid="header" className="fixed top-0 w-full z-50 glass border-b border-forest/10 h-20">
      <div className="container-custom h-full flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 group" data-testid="header-logo">
          <div className="w-10 h-10 bg-forest rounded-full flex items-center justify-center group-hover:scale-105 transition-transform">
            <Leaf className="w-5 h-5 text-cream" />
          </div>
          <span className="font-playfair text-2xl font-bold text-dark">FarmBox</span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={`font-manrope font-medium transition-colors hover:text-forest ${
                location.pathname === link.href ? 'text-forest' : 'text-dark/70'
              }`}
              data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
            >
              {link.label}
            </Link>
          ))}
          <span className="w-px h-5 bg-forest/15" />
          <Link
            to="/for-farms"
            className={`font-manrope font-medium transition-colors hover:text-forest ${location.pathname === '/for-farms' ? 'text-forest' : 'text-dark/50'}`}
            data-testid="nav-for-farms"
          >
            For Farms &amp; Producers
          </Link>
        </nav>

        {/* Right Actions */}
        <div className="flex items-center gap-4">
          {/* Cart */}
          <Link
            to="/cart"
            className="relative p-2 hover:bg-forest/5 rounded-full transition-colors"
            data-testid="cart-link"
          >
            <ShoppingCart className="w-6 h-6 text-dark" />
            {cart.count > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-gold text-dark text-xs font-bold rounded-full flex items-center justify-center" data-testid="cart-count">
                {cart.count}
              </span>
            )}
          </Link>

          {/* User Menu */}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 p-2 hover:bg-forest/5 rounded-full transition-colors" data-testid="user-menu-trigger">
                  <div className="w-8 h-8 bg-forest rounded-full flex items-center justify-center">
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                    ) : (
                      <User className="w-4 h-4 text-cream" />
                    )}
                  </div>
                  <ChevronDown className="w-4 h-4 text-dark hidden sm:block" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-white border border-forest/10">
                <div className="px-3 py-2 border-b border-forest/10">
                  <p className="font-medium text-dark text-sm">{user.name}</p>
                  <p className="text-xs text-dark/60">{user.email}</p>
                </div>
                <DropdownMenuItem asChild>
                  <Link to={dashHref} className="cursor-pointer" data-testid="dashboard-link">
                    <User className="w-4 h-4 mr-2" />
                    {dashLabel}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600" data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="hidden sm:flex items-center gap-3">
              <Link
                to="/login"
                className="font-manrope font-medium text-dark hover:text-forest transition-colors"
                data-testid="login-link"
              >
                Sign In
              </Link>
              <Link
                to="/register"
                className="btn-primary py-2.5 px-6 text-sm"
                data-testid="register-link"
              >
                Get Started
              </Link>
            </div>
          )}

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 hover:bg-forest/5 rounded-full transition-colors"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6 text-dark" />
            ) : (
              <Menu className="w-6 h-6 text-dark" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden absolute top-20 left-0 right-0 bg-cream border-b border-forest/10 shadow-lg" data-testid="mobile-menu">
          <nav className="container-custom py-6 flex flex-col gap-4">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`font-manrope font-medium py-2 transition-colors hover:text-forest ${
                  location.pathname === link.href ? 'text-forest' : 'text-dark/70'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/for-farms"
              className="font-manrope font-medium py-2 text-dark/60 border-t border-forest/10 pt-4"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-nav-for-farms"
            >
              For Farms &amp; Producers
            </Link>
            {!user && (
              <div className="flex flex-col gap-3 pt-4 border-t border-forest/10">
                <Link
                  to="/login"
                  className="font-manrope font-medium text-dark hover:text-forest transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className="btn-primary py-3 text-center"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Get Started
                </Link>
              </div>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
